/* eslint-disable @typescript-eslint/no-explicit-any */
import { PASS, runInterceptors } from '@/api/mutator/fetchInterceptors';
import { refreshSession } from '@/api/mutator/refreshSession';
import { isSessionExpiredResponse, notifySessionExpired } from '@/api/mutator/sessionExpired';

const placementInstance = async <T>(config: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: any;
  data?: any;
  headers?: any;
  responseType?: string;
  signal?: AbortSignal;
}): Promise<T> => {
  const intercepted = await runInterceptors({
    url: config.url,
    method: config.method,
    params: config.params,
    data: config.data,
  });
  if (intercepted !== PASS) return intercepted as T;

  const headers: {
    'content-type': string;
    'x-forwarded-authorization'?: string;
    'x-client-type'?: string;
    'CF-Access-Client-Id'?: string;
    'CF-Access-Client-Secret'?: string;
  } = {
    'content-type': 'application/json',
    // This is the API key for the placement service
    'x-forwarded-authorization': `Bearer ${import.meta.env.VITE_API_X_FORWARDED_AUTHORIZATION}`,
    'x-client-type': 'web',
  };

  const cfAccessClientId = import.meta.env.VITE_CF_ACCESS_CLIENT_ID;
  const cfAccessClientSecret = import.meta.env.VITE_CF_ACCESS_CLIENT_SECRET;

  if (cfAccessClientId && cfAccessClientSecret) {
    headers['CF-Access-Client-Id'] = cfAccessClientId;
    headers['CF-Access-Client-Secret'] = cfAccessClientSecret;
  }

  const qs = new URLSearchParams(config.params).toString();
  const queryString = qs ? `?${qs}` : '';
  const send = () =>
    fetch(`${import.meta.env.VITE_API_BASE_URL}${config.url}${queryString}`, {
      method: config.method,
      signal: config.signal,
      headers: headers,
      // Auth is now a session cookie, not a bearer token. Front and API are cross-site, so the
      // BEARER and refresh_token cookies are SameSite=None: CSRF protection relies entirely on the
      // API's CORS config not allowing credentialed requests from arbitrary origins.
      credentials: 'include',
      ...(config.data ? { body: JSON.stringify(config.data) } : {}),
    });

  try {
    let response = await send();

    // The JWT is short-lived: on a 401, renew it once with the refresh_token cookie and replay the
    // request. Only a failed refresh (no or expired refresh token, advisor session) logs the user out.
    if (isSessionExpiredResponse(response.status, config.url)) {
      if (await refreshSession(headers)) {
        response = await send();
      }
      if (isSessionExpiredResponse(response.status, config.url)) {
        notifySessionExpired();
        throw new Error('Session expired');
      }
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    return (await response.json()) as T;
  } catch (error: unknown) {
    throw error instanceof Error ? error : new Error('API request failed');
  }
};

export default placementInstance;
